/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState } from "react";
import { TouchableOpacity, View } from "react-native";
import Icon from "react-icons-kit";
import { chevronDown } from "react-icons-kit/fa/chevronDown";
import { chevronRight } from "react-icons-kit/fa/chevronRight";

import { IssueCategoryType } from "graphql/internal/generated";
import { Text } from "UpduoLibrary";
import {
  skaleBlack,
  upduoSecondLightestGray,
  skaleDeactiveGray,
  skalePrimaryBlue,
} from "config/UpduoColors";
import CategoryMenuOption from "./CategoryMenuOption";

const categoryChoices: { value: IssueCategoryType; description: string }[] = [
  {
    value: IssueCategoryType.NoSelfAv,
    description: "I couldn't see/hear my partner",
  },
  {
    value: IssueCategoryType.NoPartnerAv,
    description: "My partner couldn't see/hear me",
  },
  {
    value: IssueCategoryType.LowQuality,
    description: "Poor audio/video quality",
  },
  {
    value: IssueCategoryType.Disconnection,
    description: "The session disconnected",
  },
  {
    value: IssueCategoryType.ContentFailure,
    description: "Problem with app visuals/controls",
  },
  {
    value: IssueCategoryType.ReportPartner,
    description: "Improper partner behavior",
  },
  { value: IssueCategoryType.Other, description: "Other" },
];

const categoryMenuHeight = 49;

interface CategoryMenuProps {
  categoryChoice?: IssueCategoryType;
  onFinish: (choice: IssueCategoryType) => void;
}

export default function CategoryMenu({
  categoryChoice,
  onFinish,
}: CategoryMenuProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <TouchableOpacity
      style={{
        marginTop: 8,
        height: categoryMenuHeight,
        marginBottom: 20,
        zIndex: 100,
      }}
      onPress={() => setIsExpanded((value) => !value)}
    >
      <View
        style={{
          flex: 1,
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 10,
          borderColor: upduoSecondLightestGray,
          borderTopWidth: 1,
          borderLeftWidth: 1,
          borderRightWidth: 1,
          borderBottomWidth: isExpanded ? 0 : 1,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          borderBottomLeftRadius: isExpanded ? 0 : 8,
          borderBottomRightRadius: isExpanded ? 0 : 8,
        }}
      >
        <div style={{ color: skalePrimaryBlue }}>
          <Icon
            icon={isExpanded ? chevronDown : chevronRight}
            style={{ marginTop: 4, marginBottom: 4, marginRight: 8 }}
            size={15}
          />
        </div>
        <Text
          style={{
            color: categoryChoice ? skaleBlack : skaleDeactiveGray,
            marginLeft: 5,
          }}
        >
          {categoryChoices.find((item) => item.value === categoryChoice)
            ?.description || "Select an issue"}
        </Text>
      </View>
      {isExpanded ? (
        <View
          style={{
            position: "absolute",
            top: categoryMenuHeight,
            left: 0,
            right: 0,
          }}
        >
          {categoryChoices.map((item, index) => (
            <CategoryMenuOption
              key={item.value}
              value={item.value}
              description={item.description}
              onFinish={(choice) => {
                onFinish(choice);
                setIsExpanded(false);
              }}
              isLast={index >= categoryChoices.length - 1}
            />
          ))}
        </View>
      ) : null}
    </TouchableOpacity>
  );
}
